import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import translation from './agreements.i18n';
import { ConfirmationDialog } from '../../components/dialog';
import {
  PraksisavtaleStedBestillingspunktTypeDto,
  PraksisavtalestedValideringfeiltypeDto,
} from '../../api/generated/studiested-generated-api';
import { UhaUserContext } from '../../user/UhaUserStore';
import {
  BrukerrollerDto,
  PraksisavtaleStedDtoKapasitetstypeDtoEnum,
} from '../../api/generated/praksissted-generated-api';
import Icon, { IconType } from '../../components/icon';
import * as css from './agreements.module.scss';
import {
  DropdownButton,
  DropdownButtonOption,
} from '../../components/button/dropdownButton';
import { TableCellTd, TableRow } from '../../components/table/alpTable';
import { NavLink } from 'react-router-dom';
import {
  OnStedHoverCard,
  StedType,
} from '../../components/hierarchy/onhoverhierarchyinfo';
import { NumberInput } from '../../components/input';
import {
  AgreementTableContext,
  flattenAvtaleSteder,
  useAgreementTableRowContext,
} from './methods';
import { AvtaleTableHeader, FlyttKapasitetDialogProps } from './types';
import { formatToSummaryString } from '../../util/stringutils';
import { Language, LanguageContext } from '../../i18n/LanguageStore';
import { InformationDialog } from '../../components/dialog/informationDialog';
import classNames from 'classnames';
import { filterNotUndefinedOrNull } from '../../util/listutils';
import { WarningIconWithLabel } from '../../components/icon/iconWithLabel';
import OpenInNewTab from '../../components/link/openInNewTab';
import { useFormatMessageFor } from '../../i18n/translation';

const AgreementTableRow = () => {
  const { initSted, rowState, avtaleKey } = useAgreementTableRowContext();

  const {
    delt,
    flyttKapasitetspunktTil,
    haveOvernodeOrSiblingOrUndernodeWithFordeling,
    haveOvernodeOrSiblingOrUndernodeWithOnske,
  } = useContext(AgreementTableContext);

  const { id, navn, valideringsfeil } = initSted;

  const {
    deaktivert,
    deaktivertAvForelderKapasitet,
    hasDeaktivertForelder,
    kapasitetspunkt,
    kapasitetstype,
  } = rowState;

  const [capacityInputActive, setCapacityInputActive] = useState<boolean>(
    kapasitetstype === PraksisavtaleStedDtoKapasitetstypeDtoEnum.Studiested,
  );

  const [changeCapacityModalOpen, setChangeCapacityModalOpen] =
    useState<boolean>(false);
  const [moveCapacityPointModalOpen, setMoveCapacityPointModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    const isStudiestedKapasitet =
      kapasitetstype === PraksisavtaleStedDtoKapasitetstypeDtoEnum.Studiested;

    setCapacityInputActive((prevState) => {
      if (!kapasitetspunkt) return false;

      if (prevState) {
        if (!isStudiestedKapasitet) return false;
      } else {
        if (isStudiestedKapasitet) return true;
      }

      return prevState;
    });
  }, [kapasitetstype, kapasitetspunkt]);

  const undernoderMedKapasitet = Object.values(
    flattenAvtaleSteder(initSted.undernoder, initSted.avtaleId),
  ).filter((sted) => sted.kapasitetspunkt && sted.kapasitet);

  const enableCapacityInput = (confirmed: boolean) => {
    setCapacityInputActive(
      confirmed ||
        kapasitetstype === PraksisavtaleStedDtoKapasitetstypeDtoEnum.Studiested,
    );
    setChangeCapacityModalOpen(false);
  };

  const confirmMoveCapacityPoint = (confirmed: boolean) => {
    if (confirmed) flyttKapasitetspunktTil(avtaleKey);

    setMoveCapacityPointModalOpen(false);
  };

  const undernoderMedKapasitetNavn = useMemo(
    () => undernoderMedKapasitet.map((undernode) => undernode.navn),
    [undernoderMedKapasitet],
  );

  const isDeaktivert = useMemo(
    () => deaktivert || deaktivertAvForelderKapasitet || hasDeaktivertForelder,
    [deaktivert, deaktivertAvForelderKapasitet, hasDeaktivertForelder],
  );

  const rowBackground = useMemo(() => {
    if (isDeaktivert) return 'deactivated';
    return valideringsfeil ? 'alert' : 'default';
  }, [isDeaktivert, valideringsfeil]);

  return (
    <>
      <TableRow
        id={`agreementRow-${avtaleKey}`}
        rowBackground={rowBackground}
        key={id}
      >
        <PraksisstedCell />
        <AvtaleCell />
        <StatusCell />
        <BestillingCell />
        <KapasitetCell active={capacityInputActive} />
        <HandlingerCell
          setMoveCapacityPointModalOpen={setMoveCapacityPointModalOpen}
          setChangeCapacityModalOpen={setChangeCapacityModalOpen}
          capacityInputActive={capacityInputActive}
        />
      </TableRow>
      <FlyttKapasitetDialog
        isOpen={moveCapacityPointModalOpen}
        onClose={confirmMoveCapacityPoint}
        flyttTil={navn}
        delt={delt}
        hasOvernodeOrSiblingOrUndernodeWithFordeling={haveOvernodeOrSiblingOrUndernodeWithFordeling.includes(
          avtaleKey,
        )}
        hasOvernodeOrSiblingOrUndernodeWithOnske={haveOvernodeOrSiblingOrUndernodeWithOnske.includes(
          avtaleKey,
        )}
        undernoderMedKapasitetNavn={undernoderMedKapasitetNavn}
      />
      <OverwriteCapacityNotification
        isOpen={changeCapacityModalOpen}
        name={initSted.navn}
        onClose={enableCapacityInput}
      />
    </>
  );
};

const PraksisstedCell = () => {
  const { toggleExpanded } = useContext(AgreementTableContext);
  const {
    initSted: {
      topNode,
      navn,
      indent,
      id,
      periode,
      undernoder,
      bestillingIsAtTop,
    },
    headers,
    rowState: { isExpanded },
    avtaleKey,
  } = useAgreementTableRowContext();

  const rowIcon = useCallback(
    (rowId: string) => {
      if (undernoder.length > 0) {
        const buttonIcon = (type: IconType) => (
          <button
            onClick={() => toggleExpanded(avtaleKey, bestillingIsAtTop)}
            className={css.arrowIcon}
          >
            <Icon type={type} htmlId={rowId} />
          </button>
        );

        return isExpanded
          ? buttonIcon(IconType.CloseRowTriangle)
          : buttonIcon(IconType.OpenRow);
      } else {
        return (
          <div className={css.endNodeIcon}>
            <Icon type={IconType.EndNode} htmlId={rowId} />
          </div>
        );
      }
    },
    [undernoder, isExpanded, avtaleKey, bestillingIsAtTop, toggleExpanded],
  );

  return (
    <TableCellTd
      header={headers[AvtaleTableHeader.PRAKSISSTED]}
      className={css.practiceplaceNameCell}
    >
      <div className={css.practiceplace}>
        <div
          className={classNames(
            css.practiceplaceName,
            topNode && css.practiceplaceNameStrong,
          )}
          style={{ marginLeft: `${1.5 * indent + 3}em` }}
        >
          {rowIcon(id)}
          <NavLink to={`/practiceplaces/profile/${id}`}>{navn}</NavLink>
          {periode ? ` - ${periode}` : ''}
        </div>
        <OnStedHoverCard sted={StedType.PRAKSISSTED} id={id} navn={navn} />
      </div>
    </TableCellTd>
  );
};

const AvtaleCell = () => {
  const {
    initSted: { avtaleId, valideringsfeil, avtalenavn },
    headers,
  } = useAgreementTableRowContext();

  const labels = useFormatMessageFor(translation);

  return (
    <TableCellTd
      header={headers[AvtaleTableHeader.AVTALE]}
      className={css.avtaleCell}
    >
      <div className={css.avtaleCellWrapper}>
        <OpenInNewTab
          to={`/practiceplaces/practiceplaceagreement/${avtaleId}`}
          label={labels.showAvtale}
          title={avtalenavn}
        />
        {valideringsfeil && (
          <WarningIconWithLabel
            label={labels.avtaleError}
            title={valideringsfeil.message}
          />
        )}
      </div>
    </TableCellTd>
  );
};

const StatusCell = () => {
  const {
    avtaleKey,
    rowState: { deaktivertAvForelderKapasitet, deaktivert, skjult },
    headers,
  } = useAgreementTableRowContext();

  const { locale } = useIntl();
  const labels = useFormatMessageFor(translation);

  const status = useMemo(() => {
    const statusLabels: string[] = [];

    if (deaktivertAvForelderKapasitet) {
      if (deaktivert) {
        statusLabels.push(labels.deactivatedPraksissted);
      }

      statusLabels.push(
        deaktivert
          ? labels.inactiveByCapacity.toLowerCase()
          : labels.inactiveByCapacity,
      );
    } else if (deaktivert) {
      statusLabels.push(labels.deactivatedPraksissted);
    } else {
      statusLabels.push(labels.activePraksissted);
    }

    if (skjult) {
      statusLabels.push(labels.hiddenPraksissted);
    }

    return formatToSummaryString(statusLabels, locale as Language);
  }, [deaktivert, deaktivertAvForelderKapasitet, skjult, locale, labels]);

  return (
    <TableCellTd
      header={headers[AvtaleTableHeader.STATUS]}
      className={css.statusCell}
      id={`status-${avtaleKey}`}
    >
      {status}
    </TableCellTd>
  );
};

const BestillingCell = () => {
  const { onBestillingChange, kanEndre, addAvtalestedWithoutAdmin } =
    useContext(AgreementTableContext);

  const {
    initSted: {
      id,
      avtaleId,
      navn,
      harRegistrertAdminBruker,
      bestillingspunkt,
    },
    rowState: {
      deaktivert,
      deaktivertAvForelderKapasitet,
      bestilling,
      bestillingsmodus,
    },
    headers,
  } = useAgreementTableRowContext();

  const addAgreementRowWithoutAdmin = useCallback(
    (value: number | undefined) =>
      !harRegistrertAdminBruker && addAvtalestedWithoutAdmin(navn, value),
    [harRegistrertAdminBruker, navn, addAvtalestedWithoutAdmin],
  );

  return (
    <TableCellTd
      header={headers[AvtaleTableHeader.BESTILLING]}
      className={css.bestillingCell}
    >
      {bestillingsmodus === bestillingspunkt &&
        !(deaktivert || deaktivertAvForelderKapasitet) && (
          <NumberInput
            disabled={!kanEndre}
            ariaLabel={'Bestilling'}
            htmlId="placesOrdered"
            value={bestilling}
            onChange={(value) => {
              addAgreementRowWithoutAdmin(value);
              onBestillingChange(avtaleId, id, value);
            }}
          />
        )}
    </TableCellTd>
  );
};

const DefaultKapasitet = () => {
  const {
    rowState: { deaktivertAvForelderKapasitet, kapasitetspunkt, kapasitet },
  } = useAgreementTableRowContext();

  if (deaktivertAvForelderKapasitet) return <></>;

  return (
    <>
      {kapasitetspunkt ? (
        ''
      ) : (
        <span title={'Sum'} className={css.sigmaIcon}>
          Σ
        </span>
      )}
      {kapasitet || 0}
    </>
  );
};

const KapasitetCell: React.FC<React.PropsWithChildren<{ active: boolean }>> = ({
  active,
}) => {
  const { kanEndre, onKapasitetChange } = useContext(AgreementTableContext);

  const {
    initSted: { avtaleId, id },
    rowState: { kapasitet },
    headers,
  } = useAgreementTableRowContext();

  return (
    <TableCellTd
      header={headers[AvtaleTableHeader.KAPASITET]}
      className={css.kapasitetCell}
    >
      {active ? (
        <NumberInput
          ariaLabel={'Foreslått kapasitet'}
          htmlId="capacitySuggested"
          value={kapasitet}
          onChange={(value) => {
            onKapasitetChange(avtaleId, id, value);
          }}
          disabled={!kanEndre}
        />
      ) : (
        <DefaultKapasitet />
      )}
    </TableCellTd>
  );
};

const HandlingerCell: React.FC<
  React.PropsWithChildren<{
    setMoveCapacityPointModalOpen: (open: boolean) => void;
    setChangeCapacityModalOpen: (open: boolean) => void;
    capacityInputActive: boolean;
  }>
> = ({
  setMoveCapacityPointModalOpen,
  setChangeCapacityModalOpen,
  capacityInputActive,
}) => {
  const {
    kanFlytteKapasitetTilSteder,
    kanFlytteBestillinger,
    toggleBestillingsmodus,
    delt,
    handleDeaktivertPraksissted,
    handleSkjultPraksissted,
  } = useContext(AgreementTableContext);

  const {
    initSted: { id, avtaleId, valideringsfeil, bestillingspunkt, topNode },
    rowState: {
      kapasitetspunkt,
      bestillingsmodus,
      kapasitetstype,
      deaktivert,
      deaktivertAvForelderKapasitet,
      hasDeaktivertForelder,
      skjult,
      hasSkjultForelder,
    },
    headers,
    avtaleKey,
  } = useAgreementTableRowContext();
  const { uhaUser: bruker } = useContext(UhaUserContext);

  const canWrite = useMemo(
    () => bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet,
    [bruker],
  );

  const labels = useFormatMessageFor(translation);

  const handlingDisabled = useMemo<boolean>(
    // Added as own useMemo for simple extension by e.g. ALP-2082
    () => !!valideringsfeil,
    [valideringsfeil],
  );

  const feilIsOnDeaktivert = useMemo(
    () =>
      valideringsfeil?.errorType ===
      PraksisavtalestedValideringfeiltypeDto.DeaktivertSted,
    [valideringsfeil],
  );

  const shouldBeAbleToCorrectDeaktivertError = useMemo(
    () => feilIsOnDeaktivert && !deaktivert,
    [feilIsOnDeaktivert, deaktivert],
  );

  const kanFlytteKapasitetTil = useMemo(
    () => kanFlytteKapasitetTilSteder.includes(avtaleKey),
    [kanFlytteKapasitetTilSteder, avtaleKey],
  );

  const flyttKapasitetspunktItem = useMemo(() => {
    if (kanFlytteKapasitetTil) {
      return {
        disabled: kapasitetspunkt || handlingDisabled,
        text: labels.moveKapasitet,
        icon: IconType.ArrowBoxRight,
        onChange: () => setMoveCapacityPointModalOpen(true),
      };
    }
  }, [
    kanFlytteKapasitetTil,
    kapasitetspunkt,
    labels,
    setMoveCapacityPointModalOpen,
    handlingDisabled,
  ]);

  const kanFlytteBestilling = useMemo(
    () => kanFlytteBestillinger.includes(avtaleId),
    [kanFlytteBestillinger, avtaleId],
  );

  const flyttBestillingspunktItem = useMemo(() => {
    const bestillingsmodusAvtaleSted =
      bestillingsmodus === PraksisavtaleStedBestillingspunktTypeDto.AvtaleSted;

    const bestillingspunktOmfattetAvtaleSted =
      bestillingspunkt ===
      PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted;

    const canShowFlyttBestillinger =
      topNode || bestillingspunktOmfattetAvtaleSted;

    if (canShowFlyttBestillinger) {
      if (topNode) {
        const text = bestillingsmodusAvtaleSted
          ? `${labels.moveBestillingTop} ${labels.omfattedeSteder}`
          : labels.moveBestilling;

        return {
          text,
          disabled: !kanFlytteBestilling || handlingDisabled,
          icon: bestillingsmodusAvtaleSted
            ? IconType.SubdirectoryArrowLeft
            : IconType.ArrowBoxLeft,
          onChange: () => toggleBestillingsmodus(avtaleId),
        };
      } else {
        return {
          disabled:
            !kanFlytteBestilling ||
            !bestillingsmodusAvtaleSted ||
            handlingDisabled,
          text: labels.moveBestilling,
          icon: IconType.ArrowBoxLeft,
          onChange: () => toggleBestillingsmodus(avtaleId),
        };
      }
    }
  }, [
    bestillingsmodus,
    bestillingspunkt,
    topNode,
    labels,
    kanFlytteBestilling,
    toggleBestillingsmodus,
    avtaleId,
    handlingDisabled,
  ]);

  const meldInnKapasitetItem = useMemo(() => {
    if (kapasitetspunkt) {
      const disableOverwriteCapacity =
        (kapasitetspunkt &&
          kapasitetstype ===
            PraksisavtaleStedDtoKapasitetstypeDtoEnum.Studiested) ||
        capacityInputActive ||
        deaktivert ||
        deaktivertAvForelderKapasitet;

      return {
        text: labels.overwriteCapacity,
        icon: IconType.Create,
        onChange: () => setChangeCapacityModalOpen(true),
        disabled: disableOverwriteCapacity || handlingDisabled,
      };
    }
  }, [
    kapasitetspunkt,
    kapasitetstype,
    capacityInputActive,
    deaktivert,
    deaktivertAvForelderKapasitet,
    labels,
    setChangeCapacityModalOpen,
    handlingDisabled,
  ]);

  const deaktiverPraksisstedItem = useMemo(
    () => ({
      disabled:
        !shouldBeAbleToCorrectDeaktivertError &&
        (hasDeaktivertForelder || (!deaktivert && delt)),
      text: deaktivert
        ? labels.activatePraksissted
        : labels.deactivatePraksissted,
      icon: IconType.PowerSettingsNewIcon,
      onChange: () => handleDeaktivertPraksissted(avtaleKey),
      borderTop: true,
    }),
    [
      deaktivert,
      delt,
      labels,
      handleDeaktivertPraksissted,
      avtaleKey,
      hasDeaktivertForelder,
      shouldBeAbleToCorrectDeaktivertError,
    ],
  );

  const skjulPraksisstedForStudentItem = useMemo(
    () => ({
      disabled: hasSkjultForelder || feilIsOnDeaktivert,
      text: skjult ? labels.showPraksissted : labels.hidePraksissted,
      icon: IconType.PersonAddDisabledIcon,
      onChange: () => handleSkjultPraksissted(avtaleKey),
    }),
    [
      skjult,
      labels,
      handleSkjultPraksissted,
      avtaleKey,
      hasSkjultForelder,
      feilIsOnDeaktivert,
    ],
  );

  const dropdownItems = useMemo<DropdownButtonOption[]>(
    () =>
      filterNotUndefinedOrNull([
        flyttKapasitetspunktItem,
        flyttBestillingspunktItem,
        meldInnKapasitetItem,
        deaktiverPraksisstedItem,
        skjulPraksisstedForStudentItem,
      ]),
    [
      flyttKapasitetspunktItem,
      flyttBestillingspunktItem,
      meldInnKapasitetItem,
      skjulPraksisstedForStudentItem,
      deaktiverPraksisstedItem,
    ],
  );

  return (
    <TableCellTd
      header={headers[AvtaleTableHeader.HANDLINGER]}
      className={css.actionCell}
    >
      {canWrite && (
        <DropdownButton
          id={id}
          btnText={labels.actionButtonText}
          options={dropdownItems}
          disabled={handlingDisabled && !shouldBeAbleToCorrectDeaktivertError}
        />
      )}
    </TableCellTd>
  );
};

const FlyttKapasitetDialog: React.FC<
  React.PropsWithChildren<FlyttKapasitetDialogProps>
> = ({
  isOpen,
  onClose,
  flyttTil,
  delt,
  hasOvernodeOrSiblingOrUndernodeWithOnske,
  hasOvernodeOrSiblingOrUndernodeWithFordeling,
  undernoderMedKapasitetNavn,
}) => {
  const { selectedLanguage } = useContext(LanguageContext);

  const {
    confirmFlyttKapasitetTitle: title,
    confirmFlyttKapasitetSubtitle: subtitle,
    confirmFlyttKapasitetSubmitLabel: submitLabel,
    ...labels
  } = useFormatMessageFor({
    ...translation,
    confirmFlyttKapasitetTitle: {
      ...translation.confirmFlyttKapasitetTitle,
      values: { level: flyttTil },
    },
  });

  const contentLabel = useMemo(() => {
    if (delt) {
      return (
        <>
          {undernoderMedKapasitetNavn.length > 0 ? (
            <p>
              {labels.confirmFlyttKapasitetSubmitContentDeltPt1}{' '}
              <b>
                {formatToSummaryString(
                  undernoderMedKapasitetNavn,
                  selectedLanguage,
                )}
              </b>
              . {labels.confirmFlyttKapasitetSubmitContentDeltPt2}
            </p>
          ) : (
            <p>{`${labels.confirmFlyttKapasitetSubmitContent} ${labels.confirmFlyttKapasitetSubmitContentDeltMailWarning}`}</p>
          )}
          {hasOvernodeOrSiblingOrUndernodeWithOnske && (
            <p>
              <b>{labels.confirmFlyttKapasitetSubmitContentOnskeliste}</b>
            </p>
          )}
        </>
      );
    }

    return <p>{labels.confirmFlyttKapasitetSubmitContent}</p>;
  }, [
    delt,
    hasOvernodeOrSiblingOrUndernodeWithOnske,
    labels,
    selectedLanguage,
    undernoderMedKapasitetNavn,
  ]);

  return hasOvernodeOrSiblingOrUndernodeWithFordeling ? (
    <InformationDialog
      isOpen={isOpen}
      title={title}
      subtitle={subtitle}
      onClose={() => onClose(false)}
    >
      <p>{labels.confirmFlyttKapasitetSubmitContentFordeling}</p>
    </InformationDialog>
  ) : (
    <ConfirmationDialog
      isOpen={isOpen}
      title={title}
      subtitle={subtitle}
      submitLabel={submitLabel}
      onClose={onClose}
    >
      {contentLabel}
    </ConfirmationDialog>
  );
};

const OverwriteCapacityNotification: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: (confirmed: boolean) => void;
    name?: string;
  }>
> = ({ isOpen, onClose, name }) => {
  const {
    overwriteCapacityConfirm: confirm,
    overwriteCapacityAddCapacityFor: overwriteFor,
    ...labels
  } = useFormatMessageFor({
    ...translation,
    overwriteCapacityAddCapacityFor: {
      ...translation.overwriteCapacityAddCapacityFor,
      values: { name: name || '' },
    },
    overwriteCapacityDesc1: {
      ...translation.overwriteCapacityDesc1,
      values: { name: name || '' },
    },
  });

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      submitLabel={confirm}
      title={overwriteFor}
      onClose={onClose}
    >
      <p>{labels.overwriteCapacityDesc1}</p>
      <p>{labels.overwriteCapacityDesc2}</p>
      <p>{labels.overwriteCapacityDesc3}</p>
    </ConfirmationDialog>
  );
};

export default AgreementTableRow;
